import { useCallback, useRef, useState } from 'react'

import {
  SALARY_PILL_ENTER,
  SALARY_PILL_LEARN_MORE,
  SALARY_HELP_TEXT,
  SALARY_ASK,
  SALARY_RETRY,
  SALARY_OUT_OF_RANGE,
  SALARY_SAVE_FAILED,
  salaryConfirmation,
  parseSalary,
  isSalaryInRange,
} from './salaryPrompt'
import { userTurn as user, botTurn as bot } from './chatTurns'

/**
 * The client-side placeholder-salary conversation (see salaryPrompt.js).
 *
 * Returns the turns it has produced and a `handle` that the surface calls BEFORE
 * its normal send. `handle` returns true when it has taken the message, meaning
 * the agent must not see it.
 *
 * The turns outlive `enabled`. A successful save clears the alert that turned
 * this on, and the confirmation the user is reading must not vanish underneath
 * them when it does — `enabled` gates only whether new input is intercepted.
 *
 * @param {boolean}  enabled  the placeholder-salary alert is the live one
 * @param {Function} onSave   async (amount) => void; writes and re-pulls the case
 */
export default function useSalaryPrompt({ enabled, onSave }) {
  const [turns, setTurns] = useState([])
  // Whether the next free-text message is an answer to SALARY_ASK.
  const [awaitingAmount, setAwaitingAmount] = useState(false)
  const [saving, setSaving] = useState(false)
  /*
   * Whether the write landed. Home watches the alert clear instead, but the
   * standalone route holds only the snapshot it entered with — this is how it
   * learns the exchange is finished and the next alert may have the pills.
   */
  const [saved, setSaved] = useState(false)
  /*
   * Read inside `handle` as well as rendered. `handle` is called straight from a
   * click/keydown, so two fast sends can both observe the pre-update state value
   * and both fire a write; the ref closes that window.
   */
  const savingRef = useRef(false)

  const append = useCallback((...added) => setTurns(prev => [...prev, ...added]), [])

  const save = useCallback(
    async amount => {
      savingRef.current = true
      setSaving(true)
      try {
        await onSave(amount)
        setSaved(true)
        append(bot(salaryConfirmation(amount)))
      } catch (err) {
        console.error('[useSalaryPrompt] failed to save the salary:', err)
        append(bot(SALARY_SAVE_FAILED))
        // Stay in the answering state so the user can just retype the number.
        setAwaitingAmount(true)
      } finally {
        savingRef.current = false
        setSaving(false)
      }
    },
    [onSave, append]
  )

  const handle = useCallback(
    text => {
      const trimmed = (text || '').trim()
      if (!trimmed) return false
      // Nothing to intercept once the alert is gone — later messages are the
      // agent's, including a second "Learn more" from some other turn's pills.
      if (!enabled) return false
      // A write is in flight; swallow rather than queue a second one.
      if (savingRef.current) return true

      // Offered at any point, including mid-answer: it is the one pill whose
      // label a user might also type, and answering it costs nothing.
      if (trimmed === SALARY_PILL_LEARN_MORE) {
        append(user(trimmed), bot(SALARY_HELP_TEXT, [SALARY_PILL_ENTER]))
        return true
      }

      if (trimmed === SALARY_PILL_ENTER) {
        append(user(trimmed), bot(SALARY_ASK))
        setAwaitingAmount(true)
        return true
      }

      if (!awaitingAmount) return false

      const amount = parseSalary(trimmed)
      if (amount === null) {
        append(user(trimmed), bot(SALARY_RETRY))
        return true
      }
      if (!isSalaryInRange(amount)) {
        append(user(trimmed), bot(SALARY_OUT_OF_RANGE))
        return true
      }

      append(user(trimmed))
      setAwaitingAmount(false)
      save(amount)
      return true
    },
    [enabled, awaitingAmount, append, save]
  )

  return { turns, handle, saving, awaitingAmount, saved }
}
